import React from 'react';
import {Link} from 'react-router-dom';
import includes from 'lodash/includes';
import capitalize from 'lodash/capitalize';
import {FormattedMessage} from 'react-intl';
import {Icon, Image, Label} from 'semantic-ui-react';
import * as ResourceApi from 'api/resources';
import {makeDBUGCImageUrl} from 'utils/DBUtils';
import Checkbox from 'components/forms/BasicFormFields/Checkbox.js';
import {
    BEAKER_USER_ROLE_ID,
    countriesBySiteId,
    findAnswerById,
    findAnswerByKey,
    findAnswers,
    findQuestion,
    getGroupName
} from './utils';
import {DefaultProfileImage} from "../../utils/ImageUtils";

// displays the `survey` answer selected by `user` to the question denoted by `questionKey`.
const AnswerDetail = ({user, survey, questionKey}) => {
  const question = findQuestion(survey.surveyQuestions, questionKey);
  const answers = findAnswers(user.questionAnswers, question.id);

  const {answer} = findAnswerById(question.surveyAnswers, answers[0]);
  return <div><FormattedMessage id={`humanology.survey.answer.${answer}`} /></div>;
};

// displays a green checkmark if answer denoted by `answerKey` is among the answers
// selected by `user` to the `survey` question denoted by `questionKey`.
const ConditionalCheckmark = ({user, survey, questionKey, answerKey}) => {
  const question = findQuestion(survey.surveyQuestions, questionKey);
  const answers = findAnswers(user.questionAnswers, question.id);

  const {id: answerId} = findAnswerByKey(question.surveyAnswers, answerKey);

  return includes(answers, answerId) && (
    <Icon
      className='hub-survey-answer-checkmark'
      name='checkmark'
      size='large' />
  );
};

export default {

  /* UserManagement users list */

  Email: props => ({
    header: <FormattedMessage id='userManagement.usersList.email' />,
    fieldName: 'email',
    detailFunc: user => {
      if (props.userMgmtConfig.filterResource === ResourceApi.Types.User) {
        return <Link to={`${props.userMgmtConfig.detailViewPath}/${user.id}`}>{user.email}</Link>;
      } else {
        // FIXME: this can be removed once we have a hub user profile page to navigate to.
        return <a href={`mailto:${user.email}`}>{user.email}</a>;
      }
    },
    canSort: props.userMgmtConfig.filterResource === ResourceApi.Types.User
  }),

  Id: props => ({
    header: <FormattedMessage id='userManagement.usersList.uid' />,
    fieldName: 'id',
    detailFunc: user => user.id,
    canSort: props.userMgmtConfig.filterResource === ResourceApi.Types.User
  }),

  Username: props => ({
    header: <FormattedMessage id='userManagement.usersList.username' />,
    fieldName: 'username',
    detailFunc: user => user.username,
    canSort: props.userMgmtConfig.filterResource === ResourceApi.Types.User
  }),

  FirstName: props => ({
    header: <FormattedMessage id='userManagement.usersList.firstName' />,
    fieldName: 'firstName',
    detailFunc: user => user.firstName,
    canSort: props.userMgmtConfig.filterResource === ResourceApi.Types.User
  }),

  LastName: props => ({
    header: <FormattedMessage id='userManagement.usersList.lastName' />,
    fieldName: 'lastName',
    detailFunc: user => user.lastName,
    canSort: props.userMgmtConfig.filterResource === ResourceApi.Types.User
  }),

  SiteId: props => ({
    header: <FormattedMessage id='userManagement.usersList.site' />,
    fieldName: 'siteId',
    detailFunc: user => user.siteId in countriesBySiteId ? countriesBySiteId[user.siteId] : ''
  }),

  UserGroup: props => ({
    header: <FormattedMessage id='userManagement.usersList.userGroup' />,
    fieldName: 'roles',
    detailFunc: user => {
      if (user.roleId === BEAKER_USER_ROLE_ID) {
        const groupName = getGroupName(user);

        return groupName.length
          ? <Label className='db-label-admin'>{capitalize(groupName)}</Label>
          : null;
      } else return null;
    },
    canSort: props.userMgmtConfig.filterResource === ResourceApi.Types.User
  }),

  VerifiedEmail: props => ({
    header: <FormattedMessage id='userManagement.usersList.verifiedEmail'/>,
    fieldName: 'verified',
    detailFunc: user => user.verified && <Icon className='verified' name='check' />
  }),

  /* Hub users list */

  Select: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.select' />,
    detailFunc: user => (
      <Checkbox
        boolean={true}
        value={false}
        options={[{
          key: 'hub-users-select',
          value: true
        }]}
        onValueChanged={selected => props.updateUserSelection(user.uid, selected)} />
    )
  }),

  Photo: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.photo' />,
    detailFunc: user => <Image fluid src={makeDBUGCImageUrl(user.profileImageId || DefaultProfileImage)} />
  }),

  UserId: props => ({
    header: <FormattedMessage id='userManagement.usersList.uid' />,
    detailFunc: user => user.uid
  }),

  FullName: props => ({
    className: 'hub-full-name-col',
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.fullName' />,
    detailFunc: user => user.fullName || [user.firstName, user.lastName].join(' ')
  }),

  AgeGroup: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.ageGroup' />,
    detailFunc: user => <AnswerDetail questionKey='AGE_RANGE' user={user} survey={props.survey} />
  }),

  Gender: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.gender' />,
    detailFunc: user => <AnswerDetail questionKey='GENDER' user={user} survey={props.survey} />
  }),

  HouseholdIncome: props => ({
    className: 'hub-hhi-col',
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.householdIncome' />,
    detailFunc: user => <AnswerDetail questionKey='HOUSEHOLD_INCOME_RANGE' user={user} survey={props.survey} />
  }),

  EmploymentStatus: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.employmentStatus' />,
    detailFunc: user => <AnswerDetail questionKey='EMPLOYMENT_STATUS' user={user} survey={props.survey} />
  }),

  MaritalStatus: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.maritalStatus' />,
    detailFunc: user => <AnswerDetail questionKey='MARITAL_STATUS' user={user} survey={props.survey} />
  }),

  HomeType: props => ({
    className: 'hub-home-type-col',
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.homeType' />,
    detailFunc: user => <AnswerDetail questionKey='LIVING_SITUATION' user={user} survey={props.survey} />
  }),

  HouseholdMembers: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.householdMembers' />,
    detailFunc: user => <AnswerDetail questionKey='HOUSEHOLD_SIZE' user={user} survey={props.survey} />
  }),

  State: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.state' />,
    detailFunc: user => user.state
  }),

  JoinDate: props => ({
    className: 'hub-join-date-col',
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.joinDate' />,
    detailFunc: user => {
      const values = {
        joinDate: user.joinDate,
        signUpBonus: props.hub.signUpBonus
      };

      return (
        <FormattedMessage
          id='humanologyHubUsers.usersList.column.joinDate.content'
          values={values} />
      );
    }
  }),

  Facebook: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.facebook' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='SOCIAL_PLATFORM_USAGE'
        answerKey='FACEBOOK' />
    )
  }),

  Instagram: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.instagram' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='SOCIAL_PLATFORM_USAGE'
        answerKey='INSTAGRAM' />
    )
  }),

  Pinterest: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.pinterest' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='SOCIAL_PLATFORM_USAGE'
        answerKey='PINTEREST' />
    )
  }),

  Snapchat: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.snapchat' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='SOCIAL_PLATFORM_USAGE'
        answerKey='SNAPCHAT' />
    )
  }),

  Twitter: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.twitter' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='SOCIAL_PLATFORM_USAGE'
        answerKey='TWITTER' />
    )
  }),

  TravelDomesticLeisure: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.travelDomesticLeisure' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='TRAVEL_DOMESTIC_LEISURE' />
    )
  }),

  TravelDomesticWork: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.travelDomesticWork' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='TRAVEL_DOMESTIC_WORK' />
    )
  }),

  TravelInternationalLeisure: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.travelInternationalLeisure' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='TRAVEL_INTERNATION_LESURE' />
    )
  }),

  TravelInternationalWork: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.travelInternationalWork' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='TRAVEL_INTERNATIONAL_WORK' />
    )
  }),

  HaveFinancialAdvisor: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.haveFinancialAdvisor' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='HAVE_FINANCIAL_ADVISOR' />
    )
  }),

  HavePersonalTrainer: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.havePersonalTrainer' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='HAVE_PERSONAL_TRAINER' />
    )
  }),

  MemberHealthClub: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.memberHealthClub' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='MEMBER_HEALTH_CLUB' />
    )
  }),

  MemberBookOrWineClub: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.memberBookOrWineClub' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='MEMBER_BOOK_OR_WINE_CLUB' />
    )
  }),

  MemberGameNight: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.memberGameNight' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='MEMBER_GAME_NIGHT' />
    )
  }),

  MemberFantasySports: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.memberFantasySports' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='MEMBER_FANTASY_SPORTS' />
    )
  }),

  EducationProfessionalDevelopment: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.educationProfessionalDevelopment' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='EDUCATION_PROFESSIONAL_DEVELOPMENT' />
    )
  }),

  EducationPersonalDevelopment: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.educationPersonalDevelopment' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='EDUCATION_PERSONAL_DEVELOPMENT' />
    )
  }),

  PurchasedLifeInsurance: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.purchasedLifeInsurance' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='PURCHASED_LIFE_INSURANCE' />
    )
  }),

  PurchasedFinancialProduct: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.purchasedFinancialProduct' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='RECENT_ACTIVITIES'
        answerKey='PURCHASED_FINANCIAL_PRODUCT' />
    )
  }),

  NeverExercise: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.neverExercise' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='HEALTH_ATTITUDE'
        answerKey='NEVER_EXERCISE' />
    )
  }),

  RarelyExercise: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.rarelyExercise' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='HEALTH_ATTITUDE'
        answerKey='RARELY_EXERCISE' />
    )
  }),

  AverageExercise: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.averageExercise' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='HEALTH_ATTITUDE'
        answerKey='AVERAGE_EXERCISE' />
    )
  }),

  FrequentlyExercise: props => ({
    header: <FormattedMessage id='humanologyHubUsers.usersList.column.frequentlyExercise' />,
    detailFunc: user => (
      <ConditionalCheckmark
        user={user}
        survey={props.survey}
        questionKey='HEALTH_ATTITUDE'
        answerKey='FREQUENTLY_EXERCISE' />
    )
  })

};



// WEBPACK FOOTER //
// ./public/pages/UserManagement/columns.jsx